"use client";

import { useCallback, useMemo, useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type AglcKind,
  type AglcLabels,
  aglcIsReady,
  AGLC_FIELDS as F,
  formatAglc,
} from "@/lib/tool-kit/legal-styles/aglc-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const AGLC_CITATION_GENERATOR_STRINGS = {
  formHeading: "Source details",
  resultsHeading: "Your AGLC4 citations",
  emptyPromptCase:
    "Enter the case name and its law report, neutral citation or court details to see your footnotes.",
  emptyPromptLegislation:
    "Enter the title and year of the Act and its jurisdiction to see your footnotes.",
  emptyPromptJournal:
    "Enter the author, title, year, journal and first page to see your footnotes.",
  emptyPromptBook:
    "Enter the author, title and year of publication to see your footnotes.",
  emptyPromptHansard:
    "Enter the jurisdiction, chamber and date of the debate to see your footnotes.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  kindLabel: "What you are citing",
  kindCase: "A case",
  kindLegislation: "An Act or regulations",
  kindJournal: "A journal article",
  kindBook: "A book",
  kindHansard: "Parliamentary debates",
  aglcLabel: "AGLC4 (4th edition, 2018)",
  footnote: "Footnote (first citation)",
  later: "Later footnote",
  ibid: "Next footnote, same source",
  bibliography: "Bibliography",
  noteNumberLabel: "Footnote number of the first citation",
  noteNumberPlaceholder: "1",
  noteNumberHint:
    "Used in the later footnote, which points back with (n 1). Left blank, it reads (n x) for you to fill in.",
  shortTitleLabel: "Short title for later footnotes (optional)",
  shortTitleHint:
    "Only a short title that you introduce in the first footnote can be used later. We add it in brackets and quotation marks.",
  caseNameLabel: "Case name",
  caseNamePlaceholder: "Pape v Federal Commissioner of Taxation",
  caseNameHint:
    "As on the first page of the decision: first plaintiff and first defendant only, surnames for individuals, no Pty Ltd punctuation. It is set in italics.",
  reportLabel: "Law report",
  reportPlaceholder: "(2009) 238 CLR 1",
  reportHint:
    "The authorised report where there is one (CLR, FCR, VR, NSWLR, Qd R, SASR, WAR). Round brackets for a volume-numbered series, square brackets for a series organised by year: [1980] VR 17.",
  neutralLabel: "Medium neutral citation (if there is no report)",
  neutralPlaceholder: "[2017] HCA 4",
  neutralHint:
    "Year, court identifier and number. AGLC4 cites the report in preference and never gives both.",
  unreportedLabel: "Court, judge and date (unreported, no neutral citation)",
  unreportedPlaceholder: "Supreme Court of Victoria, Hampel J, 29 June 1989",
  unreportedHint: "Shown in brackets after the case name.",
  pagesLabel: "Page pinpoint",
  pagesPlaceholder: "125-126",
  pagesHint:
    "A reported case is pinpointed to pages; a span is shortened for you (125–6). Ignored for a neutral citation.",
  paragraphsLabel: "Paragraph pinpoint",
  paragraphsPlaceholder: "365",
  paragraphsHint:
    "Numbers only, separated by commas; a span takes a hyphen: 50-59. We put them in square brackets.",
  judgeLabel: "Judge(s) (optional)",
  judgePlaceholder: "Hayne and Kiefel JJ",
  judgeHint:
    "Surname and abbreviated office, in brackets after the pinpoint: Mortimer J, French CJ, Gummow and Hayne JJ.",
  courtLabel: "Court (only if the report does not show it)",
  courtPlaceholder: "Privy Council",
  inTextLabel: "The case name is already in my sentence",
  inTextHint:
    "AGLC4 leaves the name out of the first footnote when the sentence gives it in full.",
  titleLabel: "Title and year of the Act or regulations",
  titlePlaceholder: "Racial Discrimination Act 1975",
  titleHint:
    "Short title, with the year of original enactment even if the title omits it. Title and year are set in italics.",
  jurisdictionLabel: "Jurisdiction",
  jurisdictionChoose: "Choose a jurisdiction",
  jurisdictionCth: "Commonwealth (Cth)",
  jurisdictionAct: "Australian Capital Territory (ACT)",
  jurisdictionNsw: "New South Wales (NSW)",
  jurisdictionNt: "Northern Territory (NT)",
  jurisdictionQld: "Queensland (Qld)",
  jurisdictionSa: "South Australia (SA)",
  jurisdictionTas: "Tasmania (Tas)",
  jurisdictionVic: "Victoria (Vic)",
  jurisdictionWa: "Western Australia (WA)",
  partsLabel: "Section or other provision (optional)",
  partsPlaceholder: "s 18C",
  partsHint:
    "s, ss, sub-s, sch, pt, div, cl, reg, r, ord. Words such as section 18C become s 18C. Type an en dash for a span of sections (ss 42–9) and a hyphen only inside a number (s 20-110).",
  authorsLabel: "Author(s)",
  authorsPlaceholder: "Andrew Edgar",
  authorsHint:
    "One per line, in the order and form on the title page. Initials are closed up (HLA Hart). More than three authors read First Author et al.",
  bodyLabel: "The author is an organisation",
  bodyHint: "Its name is used exactly as typed.",
  titleLabelWork: "Title",
  titlePlaceholderArticle:
    "Administrative regulation-making: contrasting parliamentary and deliberative legitimacy",
  titlePlaceholderBook: "Mistakes in contract law",
  titleHintWork:
    "Type it as printed; a colon separates a subtitle. We capitalise it as AGLC4 rule 1.7 asks.",
  yearStyleLabel: "Journal numbering",
  yearStyleRound: "Numbered by volume: (2017)",
  yearStyleSquare: "Organised by year: [1987]",
  yearLabel: "Year",
  yearPlaceholder: "2017",
  volumeLabel: "Volume (if any)",
  volumePlaceholder: "40",
  issueLabel: "Issue (if any)",
  issuePlaceholder: "3",
  issueHint: "A season or month is accepted: Winter, January.",
  journalLabel: "Journal",
  journalPlaceholder: "Melbourne University Law Review",
  journalHint: "In full, without “The”, never abbreviated.",
  startPageLabel: "First page of the article",
  startPagePlaceholder: "738",
  pinpointLabel: "Pinpoint (optional)",
  pinpointPlaceholder: "747-749",
  pinpointHint:
    "A page, a span (shortened for you: 747–9) or other parts such as ch 2. Not preceded by p or pg.",
  publisherLabel: "Publisher",
  publisherPlaceholder: "Hart Publishing",
  publisherHint: "As on the title page, without Pty Ltd, Co or “The”.",
  editionLabel: "Edition (optional)",
  editionPlaceholder: "2nd",
  editionHint:
    "Type the ordinal, such as 2nd or 15th. AGLC4 sets its letters in superscript; do that in your word processor.",
  hJurisdictionLabel: "Jurisdiction of the parliament",
  hJurisdictionPlaceholder: "Commonwealth",
  chamberLabel: "Chamber",
  chamberPlaceholder: "Senate",
  dateLabel: "Date of the debate",
  datePlaceholder: "YYYY-MM-DD",
  hansardPinpointLabel: "Page",
  hansardPinpointPlaceholder: "39",
  speakerLabel: "Speaker (optional)",
  speakerPlaceholder: "George Brandis, Attorney-General",
  speakerHint:
    "First and last name, no MP or Senator; add a role after a comma.",
  caseNote:
    "AGLC4: the authorised report comes first (2.2.2). A reported case is pinpointed to a page and then a [paragraph], after a comma; a neutral citation takes a paragraph only. The judge follows in brackets, then any short title.",
  caseBothNote:
    "You entered both a report and a neutral citation; AGLC4 uses the report and does not give parallel citations (2.2.7).",
  legislationNote:
    "AGLC4: the title and year are italic and the jurisdiction is roman. A regulation or rule is cited the same way (3.4). A short title is introduced in the first footnote and the pinpoint follows it in later ones.",
  journalNote:
    "AGLC4: the article title in single quotation marks, the year in round brackets for a volume-numbered journal or square brackets for a journal organised by year, then volume(issue), the journal in italics, the first page and a pinpoint. A bibliography inverts the first author and has no full stop.",
  bookNote:
    "AGLC4: the title in italics, then the publisher, edition and year in one pair of brackets, then the pinpoint with no comma. A bibliography inverts the first author and has no full stop.",
  hansardNote:
    "AGLC4 7.5.1: the jurisdiction, Parliamentary Debates in italics, the chamber, the full date, a page and, if you wish, the speaker in brackets.",
  exampleCase: {
    caseName: "Pape v Federal Commissioner of Taxation",
    report: "(2009) 238 CLR 1",
    pages: "125-126",
    paragraphs: "365",
    judge: "Hayne and Kiefel JJ",
    shortTitle: "Pape",
    noteNumber: "81",
  },
  exampleNeutral: {
    caseName: "Re Culleton [No 2]",
    neutral: "[2017] HCA 4",
    paragraphs: "57",
    judge: "Nettle J",
    noteNumber: "82",
  },
  exampleUnreported: {
    caseName: "Barton v Chibber",
    unreported: "Supreme Court of Victoria, Hampel J, 29 June 1989",
    pages: "3",
  },
  exampleLegislation: {
    title: "Racial Discrimination Act 1975",
    jurisdiction: "Cth",
    parts: "section 18C",
    shortTitle: "RDA",
    noteNumber: "12",
  },
  exampleRegulations: {
    title: "Migration Regulations 1994",
    jurisdiction: "Cth",
    parts: "regs 2.01–2.02",
  },
  exampleJournal: {
    authors: "Andrew Edgar",
    workTitle:
      "Administrative regulation-making: contrasting parliamentary and deliberative legitimacy",
    yearStyle: "round",
    year: "2017",
    volume: "40",
    issue: "3",
    journal: "Melbourne University Law Review",
    startPage: "738",
    pinpoint: "747-749",
    noteNumber: "5",
  },
  exampleBook: {
    authors: "Catharine MacMillan",
    workTitle: "Mistakes in contract law",
    publisher: "Hart Publishing",
    year: "2010",
    pinpoint: "9",
    noteNumber: "48",
  },
  exampleHansard: {
    hJurisdiction: "Commonwealth",
    chamber: "Senate",
    date: "2017-02-07",
    pinpoint: "39",
    speaker: "George Brandis, Attorney-General",
  },
} as const;

type Strings = typeof AGLC_CITATION_GENERATOR_STRINGS;

function text(
  id: string,
  label: string,
  placeholder?: string,
  hint?: string,
  wide?: boolean
): SourceField {
  return { id, kind: "text", label, placeholder, hint, wide };
}

function caseFields(s: Strings): SourceField[] {
  return [
    text(
      F.caseName,
      s.caseNameLabel,
      s.caseNamePlaceholder,
      s.caseNameHint,
      true
    ),
    text(F.report, s.reportLabel, s.reportPlaceholder, s.reportHint),
    text(F.neutral, s.neutralLabel, s.neutralPlaceholder, s.neutralHint),
    text(
      F.unreported,
      s.unreportedLabel,
      s.unreportedPlaceholder,
      s.unreportedHint,
      true
    ),
    text(F.pages, s.pagesLabel, s.pagesPlaceholder, s.pagesHint),
    text(
      F.paragraphs,
      s.paragraphsLabel,
      s.paragraphsPlaceholder,
      s.paragraphsHint
    ),
    text(F.judge, s.judgeLabel, s.judgePlaceholder, s.judgeHint),
    text(F.court, s.courtLabel, s.courtPlaceholder),
    text(F.shortTitle, s.shortTitleLabel, undefined, s.shortTitleHint),
    text(
      F.noteNumber,
      s.noteNumberLabel,
      s.noteNumberPlaceholder,
      s.noteNumberHint
    ),
    {
      id: F.inText,
      kind: "checkbox",
      label: s.inTextLabel,
      hint: s.inTextHint,
    },
  ];
}

function legislationFields(s: Strings): SourceField[] {
  return [
    text(F.title, s.titleLabel, s.titlePlaceholder, s.titleHint, true),
    {
      id: F.jurisdiction,
      kind: "select",
      label: s.jurisdictionLabel,
      options: [
        { value: "", label: s.jurisdictionChoose },
        { value: "Cth", label: s.jurisdictionCth },
        { value: "ACT", label: s.jurisdictionAct },
        { value: "NSW", label: s.jurisdictionNsw },
        { value: "NT", label: s.jurisdictionNt },
        { value: "Qld", label: s.jurisdictionQld },
        { value: "SA", label: s.jurisdictionSa },
        { value: "Tas", label: s.jurisdictionTas },
        { value: "Vic", label: s.jurisdictionVic },
        { value: "WA", label: s.jurisdictionWa },
      ],
    },
    text(F.parts, s.partsLabel, s.partsPlaceholder, s.partsHint),
    text(F.shortTitle, s.shortTitleLabel, undefined, s.shortTitleHint),
    text(
      F.noteNumber,
      s.noteNumberLabel,
      s.noteNumberPlaceholder,
      s.noteNumberHint
    ),
  ];
}

function authorFields(s: Strings): SourceField[] {
  return [
    {
      id: F.authors,
      kind: "people",
      label: s.authorsLabel,
      placeholder: s.authorsPlaceholder,
      hint: s.authorsHint,
    },
    { id: F.body, kind: "checkbox", label: s.bodyLabel, hint: s.bodyHint },
  ];
}

function journalFields(s: Strings): SourceField[] {
  return [
    ...authorFields(s),
    text(
      F.workTitle,
      s.titleLabelWork,
      s.titlePlaceholderArticle,
      s.titleHintWork,
      true
    ),
    {
      id: F.yearStyle,
      kind: "select",
      label: s.yearStyleLabel,
      options: [
        { value: "round", label: s.yearStyleRound },
        { value: "square", label: s.yearStyleSquare },
      ],
    },
    text(F.year, s.yearLabel, s.yearPlaceholder),
    text(F.volume, s.volumeLabel, s.volumePlaceholder),
    text(F.issue, s.issueLabel, s.issuePlaceholder, s.issueHint),
    text(F.journal, s.journalLabel, s.journalPlaceholder, s.journalHint),
    text(F.startPage, s.startPageLabel, s.startPagePlaceholder),
    text(F.pinpoint, s.pinpointLabel, s.pinpointPlaceholder, s.pinpointHint),
    text(F.shortTitle, s.shortTitleLabel, undefined, s.shortTitleHint),
    text(
      F.noteNumber,
      s.noteNumberLabel,
      s.noteNumberPlaceholder,
      s.noteNumberHint
    ),
  ];
}

function bookFields(s: Strings): SourceField[] {
  return [
    ...authorFields(s),
    text(
      F.workTitle,
      s.titleLabelWork,
      s.titlePlaceholderBook,
      s.titleHintWork,
      true
    ),
    text(
      F.publisher,
      s.publisherLabel,
      s.publisherPlaceholder,
      s.publisherHint
    ),
    text(F.edition, s.editionLabel, s.editionPlaceholder, s.editionHint),
    text(F.year, s.yearLabel, s.yearPlaceholder),
    text(F.pinpoint, s.pinpointLabel, s.pinpointPlaceholder, s.pinpointHint),
    text(F.shortTitle, s.shortTitleLabel, undefined, s.shortTitleHint),
    text(
      F.noteNumber,
      s.noteNumberLabel,
      s.noteNumberPlaceholder,
      s.noteNumberHint
    ),
  ];
}

function hansardFields(s: Strings): SourceField[] {
  return [
    text(F.hJurisdiction, s.hJurisdictionLabel, s.hJurisdictionPlaceholder),
    text(F.chamber, s.chamberLabel, s.chamberPlaceholder),
    {
      id: F.date,
      kind: "date",
      label: s.dateLabel,
      placeholder: s.datePlaceholder,
    },
    text(F.pinpoint, s.hansardPinpointLabel, s.hansardPinpointPlaceholder),
    text(F.speaker, s.speakerLabel, s.speakerPlaceholder, s.speakerHint, true),
  ];
}

function fieldsFor(s: Strings, kind: AglcKind): SourceField[] {
  switch (kind) {
    case "case":
      return caseFields(s);
    case "legislation":
      return legislationFields(s);
    case "journal":
      return journalFields(s);
    case "book":
      return bookFields(s);
    default:
      return hansardFields(s);
  }
}

function exampleFor(s: Strings, kind: AglcKind): SourceValues {
  switch (kind) {
    case "case":
      return s.exampleCase;
    case "legislation":
      return s.exampleLegislation;
    case "journal":
      return s.exampleJournal;
    case "book":
      return s.exampleBook;
    default:
      return s.exampleHansard;
  }
}

function emptyPromptFor(s: Strings, kind: AglcKind): string {
  switch (kind) {
    case "case":
      return s.emptyPromptCase;
    case "legislation":
      return s.emptyPromptLegislation;
    case "journal":
      return s.emptyPromptJournal;
    case "book":
      return s.emptyPromptBook;
    default:
      return s.emptyPromptHansard;
  }
}

/** The widget's strings, as the formatter's labels. */
export function aglcLabels(s: Strings): AglcLabels {
  return {
    aglc: s.aglcLabel,
    footnote: s.footnote,
    later: s.later,
    ibid: s.ibid,
    bibliography: s.bibliography,
    caseNote: s.caseNote,
    caseBothNote: s.caseBothNote,
    legislationNote: s.legislationNote,
    journalNote: s.journalNote,
    bookNote: s.bookNote,
    hansardNote: s.hansardNote,
  };
}

export default function AglcCitationGeneratorWidget() {
  const strings = useToolUi(AGLC_CITATION_GENERATOR_STRINGS);
  const [kind, setKind] = useState<AglcKind>("case");
  const fields = useMemo(() => fieldsFor(strings, kind), [strings, kind]);
  const labels = useMemo(() => aglcLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatAglc(kind, values, labels),
    [kind, labels]
  );
  const isReady = useCallback(
    (values: SourceValues) => aglcIsReady(kind, values),
    [kind]
  );
  const builderStrings = useMemo(
    () => ({ ...strings, emptyPrompt: emptyPromptFor(strings, kind) }),
    [strings, kind]
  );

  return (
    <SourceCitationBuilder
      example={exampleFor(strings, kind)}
      fields={fields}
      format={format}
      intro={
        <PillGroup
          label={strings.kindLabel}
          name="aglc-kind"
          onChange={setKind}
          options={[
            { value: "case", label: strings.kindCase },
            { value: "legislation", label: strings.kindLegislation },
            { value: "journal", label: strings.kindJournal },
            { value: "book", label: strings.kindBook },
            { value: "hansard", label: strings.kindHansard },
          ]}
          value={kind}
        />
      }
      isReady={isReady}
      strings={builderStrings}
    />
  );
}
